import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  EyeIcon,
  EyeSlashIcon,
  ClipboardIcon,
  ArrowPathIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { subscriptionApi } from '@services/api/subscription';
import toast from 'react-hot-toast';

interface ApiKeyDisplayProps {
  apiKey?: string | null;
  onRegenerate: () => void;
}

export default function ApiKeyDisplay({
  apiKey,
  onRegenerate,
}: ApiKeyDisplayProps) {
  const [showKey, setShowKey] = useState(false);
  const [copying, setCopying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Handle missing API key
  const safeApiKey = apiKey || '';
  const maskedKey =
    safeApiKey.length > 4
      ? `${'*'.repeat(safeApiKey.length - 4)}${safeApiKey.slice(-4)}`
      : '*'.repeat(safeApiKey.length || 8);
  const displayKey = showKey ? safeApiKey : maskedKey;

  const handleCopy = async () => {
    if (!safeApiKey) {
      toast.error('No API key available to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(safeApiKey);
      setCopying(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopying(false), 2000);
    } catch (error) {
      toast.error('Failed to copy API key');
    }
  };

  const handleRegenerate = async () => {
    if (
      !confirm(
        'Are you sure you want to regenerate your API key? Your old key will stop working immediately. This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      setRegenerating(true);
      await subscriptionApi.regenerateApiKey();
      toast.success('API key regenerated successfully');
      onRegenerate();
    } catch (error: any) {
      toast.error(error.message || 'Failed to regenerate API key');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white overflow-hidden shadow rounded-lg sticky top-6"
    >
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
          API Key
        </h3>

        {/* API Key Display */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Your API Key
            </label>
            <button
              onClick={() => setShowKey(!showKey)}
              className="text-sm text-indigo-600 hover:text-indigo-500 flex items-center"
            >
              {showKey ? (
                <>
                  <EyeSlashIcon className="h-4 w-4 mr-1" />
                  Hide
                </>
              ) : (
                <>
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Show
                </>
              )}
            </button>
          </div>

          <div className="relative">
            <input
              type="text"
              readOnly
              value={displayKey}
              className="block w-full rounded-md border-gray-300 bg-gray-50 pr-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={handleCopy}
            disabled={copying}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {copying ? (
              <>
                <CheckIcon className="h-4 w-4 mr-2 text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <ClipboardIcon className="h-4 w-4 mr-2" />
                Copy to Clipboard
              </>
            )}
          </button>

          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon
              className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`}
            />
            {regenerating ? 'Regenerating...' : 'Regenerate Key'}
          </button>
        </div>

        {/* Warning */}
        <div className="mt-4 p-3 bg-yellow-50 rounded-md">
          <p className="text-xs text-yellow-800">
            <strong>Warning:</strong> Keep your API key secure. Never share it
            publicly or commit it to version control.
          </p>
        </div>

        {/* Documentation Link */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <a
            href="/docs/api"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500"
          >
            <BookOpenIcon className="h-4 w-4 mr-2" />
            View API Documentation
          </a>
        </div>

        {/* Usage Instructions */}
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">How to Use</h4>
          <div className="bg-gray-50 rounded-md p-3">
            <code className="text-xs text-gray-800">
              <div className="mb-2">curl -X POST \</div>
              <div className="mb-2 ml-4">https://api.example.com/convert \</div>
              <div className="ml-4">
                -H "Authorization: Bearer{' '}
                {showKey ? safeApiKey : 'YOUR_API_KEY'}"
              </div>
            </code>
          </div>
        </div>

        {/* Key Info */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Last regenerated</span>
            <span>Never</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
